// client/src/components/ProModal.js
import React, { useState } from 'react';
import axios from 'axios';

const s = {
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24, backdropFilter:'blur(4px)' },
  modal: { background:'var(--card)', maxWidth:520, width:'100%', borderRadius:0, overflow:'hidden', border:'1px solid var(--ink)', boxShadow:'8px 8px 0 rgba(10,10,10,0.12)', animation:'fadeIn 0.25s ease', maxHeight:'90vh', overflowY:'auto' },
  header: { background:'var(--ink)', padding:'32px 36px 28px', position:'relative' },
  close: { position:'absolute', top:16, right:16, background:'transparent', border:'none', color:'var(--muted-2)', fontSize:20, cursor:'pointer', lineHeight:1 },
  badge: { display:'inline-block', border:'1px solid var(--muted)', color:'var(--muted-2)', fontSize:10, letterSpacing:'2px', padding:'3px 8px', marginBottom:16, textTransform:'uppercase', fontFamily:'var(--font-mono)' },
  title: { fontFamily:'var(--font-display)', color:'var(--paper)', fontSize:28, lineHeight:1.2, marginBottom:8 },
  subtitle: { color:'var(--muted)', fontSize:13, lineHeight:1.6 },
  body: { padding:'32px 36px' },
  plans: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:28 },
  plan: { border:'1px solid var(--line-card)', borderRadius:0, padding:20, cursor:'pointer', transition:'all 0.15s', position:'relative' },
  planActive: { border:'2px solid var(--ink)', background:'var(--paper-2)' },
  planBadge: { position:'absolute', top:-10, left:'50%', transform:'translateX(-50%)', background:'var(--ink)', color:'var(--paper)', fontSize:10, padding:'2px 10px', letterSpacing:'1px', textTransform:'uppercase', fontFamily:'var(--font-mono)', whiteSpace:'nowrap' },
  planName: { fontSize:13, fontWeight:600, marginBottom:6, letterSpacing:'0.5px', textTransform:'uppercase', fontFamily:'var(--font-mono)' },
  planPrice: { fontSize:28, fontWeight:700, fontFamily:'var(--font-mono)', lineHeight:1 },
  planPeriod: { fontSize:12, color:'var(--muted)', marginTop:4 },
  planOriginal: { fontSize:12, color:'var(--muted-2)', textDecoration:'line-through', marginTop:2 },
  planSavings: { fontSize:11, color:'#16a34a', fontWeight:600, marginTop:4 },
  divider: { borderTop:'1px solid var(--line-card)', margin:'0 0 20px' },
  sectionLabel: { fontSize:10, fontWeight:700, letterSpacing:'2px', color:'var(--muted-2)', textTransform:'uppercase', fontFamily:'var(--font-mono)', marginBottom:12 },
  features: { marginBottom:24 },
  feature: { display:'flex', alignItems:'flex-start', gap:10, padding:'7px 0', borderBottom:'1px solid var(--line-card)', fontSize:13, color:'var(--muted)', lineHeight:1.5 },
  featureMark: { width:18, height:18, background:'var(--ink)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--paper)', fontSize:10, flexShrink:0, marginTop:1 },
  ctaBtn: { width:'100%', background:'var(--ink)', color:'var(--paper)', border:'none', padding:'14px', fontSize:15, fontWeight:600, borderRadius:0, cursor:'pointer', letterSpacing:'0.3px', transition:'opacity 0.15s' },
  disclaimer: { fontSize:11, color:'var(--muted-2)', textAlign:'center', marginTop:12, lineHeight:1.5 }
};

// What Free gets
const FREE_FEATURES = [
  { text: 'Search all 574,000+ studies', included: true },
  { text: 'All filters (status, phase, date, location...)', included: true },
  { text: 'Full study detail pages', included: true },
  { text: 'Save up to 5 studies', included: true },
];

// What Pro adds
const PRO_FEATURES = [
  { text: 'Unlimited saved studies', icon: '💾' },
  { text: 'CSV export — up to 1,000 studies per search', icon: '📥' },
  { text: 'Save up to 20 named search presets', icon: '🔖' },
  { text: 'Advanced Essie query expressions', icon: '🔬' },
  { text: 'Early access to new features', icon: '🚀' },
];

export default function ProModal({ onClose, defaultPlan = 'annual' }) {
  const [plan, setPlan] = useState(defaultPlan);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post('/api/stripe/create-checkout', { plan });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <button style={s.close} onClick={onClose}>×</button>
          <div style={s.badge}>Pro Plan</div>
          <h2 style={s.title}>Unlock Pro Access</h2>
          <p style={s.subtitle}>Advanced tools for researchers, scientists, and clinicians</p>
        </div>

        <div style={s.body}>
          {/* Plans */}
          <div className="pro-plans-grid" style={s.plans}>
            <div style={{ ...s.plan, ...(plan === 'monthly' ? s.planActive : {}) }} onClick={() => setPlan('monthly')}>
              <div style={s.planName}>Monthly</div>
              <div style={s.planPrice}>$9</div>
              <div style={s.planPeriod}>per month</div>
              <div style={s.planSavings}>Billed monthly</div>
            </div>
            <div style={{ ...s.plan, ...(plan === 'annual' ? s.planActive : {}), position:'relative' }} onClick={() => setPlan('annual')}>
              <div style={s.planBadge}>Best Value</div>
              <div style={s.planName}>Annual</div>
              <div style={s.planPrice}>$99</div>
              <div style={s.planPeriod}>per year · $8.25/mo</div>
              <div style={s.planSavings}>Save $9 vs monthly</div>
            </div>
          </div>

          {/* Free vs Pro comparison */}
          <div style={s.sectionLabel}>What's included in Free</div>
          <div style={{ ...s.features, marginBottom: 20 }}>
            {FREE_FEATURES.map((f, i) => (
              <div key={i} style={s.feature}>
                <div style={{ ...s.featureMark, background: 'var(--line-card)', color: 'var(--muted)' }}>✓</div>
                <span style={{ color: 'var(--muted)' }}>{f.text}</span>
              </div>
            ))}
          </div>

          <div style={s.sectionLabel}>Pro adds all of this</div>
          <div style={s.features}>
            {PRO_FEATURES.map((f, i) => (
              <div key={i} style={s.feature}>
                <div style={s.featureMark}>✓</div>
                <span><strong>{f.text}</strong></span>
              </div>
            ))}
          </div>

          {error && <div style={{ color:'var(--danger)', fontSize:13, marginBottom:12, padding:'10px', background:'var(--paper-2)', border:'1px solid var(--danger)', borderRadius:0 }}>{error}</div>}

          <button style={{ ...s.ctaBtn, opacity: loading ? 0.7 : 1 }} onClick={handleUpgrade} disabled={loading}>
            {loading ? 'Redirecting to checkout...' : `Start ${plan === 'annual' ? 'Annual ($99/yr)' : 'Monthly ($9/mo)'} →`}
          </button>
          <p style={s.disclaimer}>Secure checkout via Stripe · Cancel anytime</p>
        </div>
      </div>
    </div>
  );
}